import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './EmailVerification.module.css';
import InputField from '../../components/inputfield/InputField';
import CustomButton from '../../components/buttons/CustomButton';
import SuccessMessage from '../../components/success-message/SuccessMessage';
import LogoImage from '../../assets/images/logo-full.png';
import {
  ResendVerificationEmailAPI,
  ChangePendingSignupEmailAPI,
  GetPendingSignupStatusAPI
} from '../../api/API';
import {
  trackVerificationResent,
  trackGoogleAdsConfirmedSignupConversion,
  afterConfirmedSignupConversion
} from '../../utils/analytics';

const RESEND_COOLDOWN_SECONDS = 60;
// Intervalo da consulta "o e-mail já foi confirmado?" enquanto a aba está visível:
// 5 s nos 2 primeiros minutos, 15 s até 10 minutos, 30 s depois disso
const STATUS_POLL_STEPS = [
  { untilMs: 2 * 60 * 1000, everyMs: 5000 },
  { untilMs: 10 * 60 * 1000, everyMs: 15000 }
];
const STATUS_POLL_SLOW_MS = 30000;
// Sem resposta da consulta até aqui, o "Já confirmei" sai do carregando com um aviso
const STATUS_CHECK_SAFETY_MS = 12000;

/** Intervalo da próxima consulta, conforme o tempo desde que a tela começou a consultar. */
function statusPollDelay(elapsedMs) {
  const step = STATUS_POLL_STEPS.find((s) => elapsedMs < s.untilMs);
  return step ? step.everyMs : STATUS_POLL_SLOW_MS;
}

/** Lê um item do sessionStorage sem quebrar a tela se ele estiver indisponível. */
function readSession(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
}

/**
 * Este navegador já entrou na conta pelo link de confirmação (outra aba)?
 * O ConfirmEmail grava a sessão no localStorage, compartilhado entre as abas, e o
 * refresh token fica no cookie: um carregamento de /dashboard renova o acesso sozinho.
 */
function hasSessionFor(email) {
  try {
    const session = JSON.parse(localStorage.getItem('sessionData') || 'null');
    const sessionEmail = session && session.user && session.user.email;
    return !!sessionEmail && !!email && sessionEmail.toLowerCase() === email.trim().toLowerCase();
  } catch (e) {
    return false;
  }
}

/** Deixa o e-mail preenchido no login (mesmo campo do "lembrar-me" do Login.js). */
function prefillLoginEmail(email) {
  if (!email) return;
  try {
    localStorage.setItem('rememberedEmail', email);
  } catch (e) {
    // localStorage indisponível - a pessoa digita o e-mail
  }
}

function PendingVerification({ onSuccess }) {
  const location = useLocation();

  const [email, setEmail] = useState(() => {
    if (location.state && location.state.email) {
      return location.state.email;
    }
    return readSession('pendingVerificationEmail');
  });

  // Chave devolvida pelo cadastro (auth/register.php) para acompanhar a confirmação
  const [statusKey] = useState(() => {
    if (location.state && location.state.statusKey) {
      return location.state.statusKey;
    }
    return readSession('pendingVerificationStatusKey');
  });
  const [confirmed, setConfirmed] = useState(false);
  // Sessão devolvida pela consulta de status (entrada direta na aba do cadastro).
  // Como no ConfirmEmail, só é ativada no App ao sair desta tela.
  const [loginPayload, setLoginPayload] = useState(null);
  const sessionActivatedRef = useRef(false);
  // Uma consulta por vez, também entre as duas montagens do efeito no StrictMode:
  // a chave da entrada direta é de uso único. Guarda o início da consulta: uma que
  // passou do tempo de segurança sem resposta deixa de contar, para nada travar.
  const statusInFlightRef = useRef(0);
  // Quem clicou "Já confirmei" durante uma consulta automática espera o resultado dela
  const statusWaitersRef = useRef([]);
  // Momento em que o link do e-mail expira (vem da consulta); depois dele a tela para
  const linkExpiresAtRef = useRef(null);
  const [linkExpired, setLinkExpired] = useState(false);
  // Incrementado quando um link novo é enviado: recomeça a consulta do zero
  const [pollRound, setPollRound] = useState(0);
  const [manualChecking, setManualChecking] = useState(false);
  const [manualCheckPending, setManualCheckPending] = useState(false);
  const [manualCheckFailed, setManualCheckFailed] = useState(false);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  const [showChangeEmailForm, setShowChangeEmailForm] = useState(false);
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);
  const [changeEmailError, setChangeEmailError] = useState(null);
  const [changeEmailSuccess, setChangeEmailSuccess] = useState(false);

  const newEmailRef = useRef(null);
  const passwordRef = useRef(null);

  // Mantém o email pendente disponível para outras telas (ex.: ConfirmEmail)
  useEffect(() => {
    if (email) {
      try {
        sessionStorage.setItem('pendingVerificationEmail', email);
      } catch (e) {
        // sessionStorage indisponível - segue sem bloquear a tela
      }
    }
  }, [email]);

  const isStatusInFlight = () =>
    statusInFlightRef.current > 0 && Date.now() - statusInFlightRef.current < STATUS_CHECK_SAFETY_MS;

  // Uma consulta de status; onDone(confirmou) é chamado quando a resposta chega.
  // Devolve false se não consultou (outra consulta em andamento).
  const checkStatus = useCallback((onDone) => {
    if (!email || !statusKey || isStatusInFlight()) return false;
    statusInFlightRef.current = Date.now();
    GetPendingSignupStatusAPI(email, statusKey, (response) => {
      statusInFlightRef.current = 0;
      const waiters = statusWaitersRef.current;
      statusWaitersRef.current = [];
      const isConfirmed = !!(response && response.status === 'confirmed');
      waiters.forEach((waiter) => waiter(isConfirmed));
      if (isConfirmed) {
        // Conversão do Google Ads: esta aba é a do navegador que veio do anúncio
        trackGoogleAdsConfirmedSignupConversion(response.conversion_id);
        // A resposta com sessão é tratada mesmo se o efeito já foi desmontado:
        // a chave foi gasta no servidor e não volta numa próxima consulta
        if (response.token && response.session) {
          setLoginPayload({ token: response.token, session: response.session });
        }
        setConfirmed(true);
        if (onDone) onDone(true);
        return;
      }
      if (response && typeof response.expires_in_seconds === 'number') {
        linkExpiresAtRef.current = Date.now() + response.expires_in_seconds * 1000;
      }
      if (onDone) onDone(false);
    });
    return true;
  }, [email, statusKey]);

  // Percebe a confirmação feita em outra aba ou aparelho: consulta enquanto a aba
  // está visível, cada vez mais espaçado, e logo que ela volta a ficar visível.
  // Para quando o link do e-mail expira (o reenvio recomeça do zero).
  useEffect(() => {
    if (!email || !statusKey || confirmed) return undefined;

    linkExpiresAtRef.current = null;
    setLinkExpired(false);
    const startedAt = Date.now();
    let timer = null;
    let stopped = false;

    const stopIfExpired = () => {
      const expiresAt = linkExpiresAtRef.current;
      if (expiresAt === null || Date.now() < expiresAt) return false;
      stopped = true;
      clearTimeout(timer);
      setLinkExpired(true);
      return true;
    };

    const check = () => {
      if (stopped || document.hidden || stopIfExpired()) return;
      checkStatus(() => {
        if (!stopped) stopIfExpired();
      });
    };

    const scheduleNext = () => {
      timer = setTimeout(() => {
        check();
        if (!stopped) scheduleNext();
      }, statusPollDelay(Date.now() - startedAt));
    };

    check();
    scheduleNext();
    document.addEventListener('visibilitychange', check);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [email, statusKey, confirmed, pollRound, checkStatus]);

  // "Já confirmei": consulta na hora, também depois que o link expirou. Com uma
  // consulta automática em andamento, aguarda o resultado dela. Sempre dá retorno.
  const handleCheckNow = () => {
    if (manualChecking) return;
    setManualCheckPending(false);
    setManualCheckFailed(false);
    setManualChecking(true);

    let answered = false;
    const finish = (wasConfirmed) => {
      if (answered) return;
      answered = true;
      clearTimeout(safetyTimer);
      setManualChecking(false);
      if (!wasConfirmed) setManualCheckPending(true);
    };
    const safetyTimer = setTimeout(() => {
      if (answered) return;
      answered = true;
      setManualChecking(false);
      setManualCheckFailed(true);
    }, STATUS_CHECK_SAFETY_MS);

    if (checkStatus(finish)) return;
    if (isStatusInFlight()) {
      statusWaitersRef.current.push(finish);
      return;
    }
    finish(false);
  };

  // Contagem regressiva do cooldown de reenvio
  useEffect(() => {
    if (cooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = () => {
    if (!email || resendLoading || cooldown > 0) return;

    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    ResendVerificationEmailAPI(email, (response) => {
      setResendLoading(false);

      if (response && response.error) {
        setResendError(response.error);
        return;
      }

      setResendMessage(
        (response && response.message) ||
          'E-mail de confirmação reenviado. Verifique sua caixa de entrada.'
      );
      trackVerificationResent();
      setCooldown(RESEND_COOLDOWN_SECONDS);
      // Link novo: a consulta recomeça e vale até a nova expiração
      setManualCheckPending(false);
      setManualCheckFailed(false);
      setPollRound((round) => round + 1);
    });
  };

  const validateChangeEmailForm = () => {
    const newEmail = newEmailRef.current ? newEmailRef.current.GetValue() : '';
    const password = passwordRef.current ? passwordRef.current.GetValue() : '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    let isValid = true;

    if (!newEmail || !emailRegex.test(newEmail)) {
      if (newEmailRef.current) newEmailRef.current.SetError('Digite um e-mail válido');
      isValid = false;
    }

    if (!password) {
      if (passwordRef.current) passwordRef.current.SetError('A senha é obrigatória');
      isValid = false;
    }

    return isValid;
  };

  const handleChangeEmail = () => {
    if (!validateChangeEmailForm()) return;

    const newEmail = newEmailRef.current.GetValue();
    const password = passwordRef.current.GetValue();

    setChangeEmailLoading(true);
    setChangeEmailError(null);

    ChangePendingSignupEmailAPI(email, password, newEmail, (response) => {
      setChangeEmailLoading(false);

      if (response && response.error) {
        if (response.code === 'INVALID_CREDENTIALS') {
          if (passwordRef.current) {
            passwordRef.current.SetError(response.error || 'Credenciais inválidas.');
          }
        } else {
          setChangeEmailError(response.error);
        }
        return;
      }

      setEmail(newEmail);
      setShowChangeEmailForm(false);
      setChangeEmailSuccess(true);
      setResendMessage(null);
      setResendError(null);
      setCooldown(0);
      if (newEmailRef.current) newEmailRef.current.Clear();
      if (passwordRef.current) passwordRef.current.Clear();
    });
  };

  // Sai desta tela: entra direto se a consulta trouxe a sessão; painel, se este
  // navegador já entrou pelo link; senão o login com o e-mail preenchido
  const handleLeaveAfterConfirmation = () => {
    try {
      sessionStorage.removeItem('pendingVerificationEmail');
      sessionStorage.removeItem('pendingVerificationStatusKey');
    } catch (e) {
      // sessionStorage indisponível - segue
    }

    // A entrada recarrega a página: espera a conversão do Google Ads sair antes
    afterConfirmedSignupConversion(() => {
      if (loginPayload) {
        if (!sessionActivatedRef.current && onSuccess) {
          sessionActivatedRef.current = true;
          onSuccess(loginPayload);
        }
        return;
      }

      if (hasSessionFor(email)) {
        window.location.href = '/dashboard';
        return;
      }
      prefillLoginEmail(email);
    });
  };

  if (confirmed) {
    const signedIn = (!!loginPayload && !!onSuccess) || hasSessionFor(email);
    return (
      <SuccessMessage
        title="E-mail confirmado"
        message={signedIn
          ? 'Sua conta está ativa. Vamos te levar para o painel.'
          : 'Sua conta está ativa. Entre com seu e-mail e a senha que você criou.'}
        redirectPath={signedIn ? '/dashboard' : '/login'}
        redirectLabel={signedIn ? 'Entrar agora' : 'Ir para o login'}
        autoRedirectDelay={5}
        onBeforeRedirect={handleLeaveAfterConfirmation}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.containerForm}>
        <div className={styles.logoContainer}>
          <img className={styles.logo} src={LogoImage} alt="Logo" />
          <h1 className={styles.title}>Confirme seu e-mail</h1>
          <p className={styles.subtitle}>
            Enviamos um link de confirmação para o seu e-mail. Confirme seu endereço para começar a usar a Gabriela.
          </p>
        </div>

        {email && <div className={styles.emailHighlight}>{email}</div>}

        {changeEmailSuccess && (
          <div className={styles.successMessage}>
            <strong>E-mail atualizado!</strong>
            Enviamos um novo link de confirmação para o novo endereço.
          </div>
        )}

        {resendMessage && <div className={styles.successMessage}>{resendMessage}</div>}
        {resendError && <div className={styles.errorMessage}>{resendError}</div>}

        <p className={styles.noteText}>
          Não chegou? Veja a caixa de spam ou promoções, ou reenvie.
        </p>

        {statusKey && !linkExpired && (
          <p className={styles.noteText}>
            Assim que você confirmar, esta tela avança sozinha.
          </p>
        )}

        {statusKey && linkExpired && (
          <div className={styles.errorMessage}>
            O link de confirmação expirou. Reenvie o e-mail para receber um novo.
          </div>
        )}

        {manualCheckPending && (
          <div className={styles.errorMessage}>
            Ainda não recebemos a confirmação. Abra o link do e-mail e tente de novo.
          </div>
        )}

        {manualCheckFailed && (
          <div className={styles.errorMessage}>
            Não foi possível conferir agora. Tente de novo em instantes.
          </div>
        )}

        {!showChangeEmailForm && (
          <div className={styles.actionsColumn}>
            <CustomButton
              variant="contained"
              style="primary"
              label={cooldown > 0 ? `Reenviar e-mail (${cooldown}s)` : 'Reenviar e-mail'}
              onClick={handleResend}
              enabled={!resendLoading && cooldown === 0 && !!email}
              loading={resendLoading}
            />
            {statusKey && (
              <CustomButton
                variant="outlined"
                style="primary"
                label="Já confirmei"
                onClick={handleCheckNow}
                enabled={!manualChecking && !!email}
                loading={manualChecking}
              />
            )}
            <CustomButton
              variant="outlined"
              style="primary"
              label="Alterar endereço de e-mail"
              onClick={() => {
                setShowChangeEmailForm(true);
                setChangeEmailError(null);
                setChangeEmailSuccess(false);
              }}
            />
          </div>
        )}

        {showChangeEmailForm && (
          <div className={styles.changeEmailForm}>
            <InputField
              Width={'100%'}
              Label={'Novo e-mail'}
              type="email"
              placeholder="Digite o novo e-mail"
              ref={newEmailRef}
            />
            <InputField
              Width={'100%'}
              Label={'Senha atual'}
              type="password"
              placeholder="Digite sua senha atual"
              ref={passwordRef}
            />

            {changeEmailError && <div className={styles.errorMessage}>{changeEmailError}</div>}

            <div className={styles.actionsColumn}>
              <CustomButton
                variant="contained"
                style="primary"
                label="Salvar novo e-mail"
                onClick={handleChangeEmail}
                enabled={!changeEmailLoading}
                loading={changeEmailLoading}
              />
              <CustomButton
                variant="text"
                style="primary"
                label="Cancelar"
                onClick={() => setShowChangeEmailForm(false)}
                enabled={!changeEmailLoading}
              />
            </div>
          </div>
        )}

        <div className={styles.backLink}>
          <Link to="/login" onClick={() => prefillLoginEmail(email)}>
            Ir para o login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PendingVerification;
